import { Tooltip, Typography } from '@mui/material';

import WarningAmberIcon from '../icons/WarningAmberIcon';
import { ActionType, Claim } from '../../types/claims';
import { SimpleColumnDef } from '../../types/table';
import AssignedUserSelect from './components/AssignedUserSelect';
import StatusChip from './components/table-components/StatusChip';
import { getDataWarningMessages } from './claimDataWarnings';
import { ACTION_TYPE_LABELS } from './components/edi-components/claimAppeals';
import { formatCurrency, formatDisplayDate, formatInstant } from '../helpers/dataHelper';

const formatActionTypes = (types?: ActionType[] | null) =>
    types?.length
        ? Array.from(new Set(types)).map((type) => ACTION_TYPE_LABELS[type] ?? type).join(', ')
        : '-';

// The Claims table's columns (plus its leading data-warnings icon column),
// shared with the home page's "Your Claims" table so both render claims alike.
export const getClaimsTableColumns = (): SimpleColumnDef<Claim>[] => [
    {
        id: 'warnings',
        header: '',
        width: 35,
        minWidth: 35,
        maxWidth: 35,
        accessorKey: 'dataWarnings',
        enableSorting: false,
        cell: (value) => {
            const warnings = getDataWarningMessages(value);
            if (!warnings.length) return null;
            return (
                <Tooltip title={warnings.join('\n')} arrow>
                    <span style={{ display: 'inline-flex', width: '100%', justifyContent: 'center' }}>
                        <WarningAmberIcon color="#D97706" size={20} />
                    </span>
                </Tooltip>
            );
        },
    },
    {
        id: 'claimNumber',
        header: 'Claim #',
        accessorKey: 'claimNumber',
        width: 140,
        minWidth: 140,
        cell: (value) => (
            <Typography
                sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                }}
            >
                {value}
            </Typography>
        ),
    },
    {
        id: 'claimType',
        header: 'Type',
        accessorKey: 'claimType',
        width: 100,
        minWidth: 100,
        cell: (value) => (
            <Typography
                sx={{
                    fontWeight: 500,
                    textAlign: 'center',
                }}
            >
                {value || '-'}
            </Typography>
        ),
    },
    {
        id: 'patientName',
        header: 'Patient',
        accessorKey: 'patientName',
        width: 140,
        minWidth: 120,
        cell: (value) => (
            <Typography>
                {value}
            </Typography>
        ),
    },
    {
        id: 'medicalRecordNumber',
        header: 'MRN',
        accessorKey: 'medicalRecordNumber',
        width: 140,
        minWidth: 140,
        cell: (value) => (
            <Typography>
                {value || '-'}
            </Typography>
        ),
    },
    {
        id: 'dob',
        header: 'DOB',
        accessorKey: 'dob',
        width: 100,
        minWidth: 100,
        cell: (value) => (
            <Typography>
                {formatDisplayDate(value)}
            </Typography>)
    },
    {
        id: 'dateOfService',
        header: 'DOS',
        accessorKey: 'dateOfService',
        width: 100,
        minWidth: 100,
        cell: (value) => (
            <Typography>
                {formatDisplayDate(value)}
            </Typography>
        ),
    },
    {
        id: 'placeOfService',
        header: 'POS',
        accessorKey: 'placeOfService',
        width: 70,
        minWidth: 70,
        cell: (value) => {
            if (!value) return <Typography>-</Typography>;
            const code = value.split(/[-–—\s]/)[0]?.trim() || value;
            return (
                <Tooltip title={value} arrow>
                    <Typography sx={{ cursor: 'default' }}>
                        {code}
                    </Typography>
                </Tooltip>
            );
        },
    },
    {
        id: 'procedureCode',
        header: 'Procedure',
        accessorKey: 'procedureCode',
        width: 100,
        minWidth: 100,
        // procedureCode is the claim's single principal procedure (ICD-10-PCS or
        // HCPCS, institutional claims only) -- never a comma-separated list, unlike
        // cptCode, so no truncation/tooltip needed here.
        cell: (value) => <Typography>{value || '-'}</Typography>,
    },
    {
        id: 'diagnosisCode',
        header: 'Diagnostic',
        accessorKey: 'diagnosisCode',
        width: 100,
        minWidth: 100,
        cell: (value) => {
            if (!value) return <Typography>-</Typography>;
            const codes = value.split(',').map((c: string) => c.trim()).filter(Boolean);
            if (codes.length <= 1) return <Typography>{codes.join(', ')}</Typography>;
            return (
                <Tooltip title={codes.join(', ')} arrow>
                    <Typography sx={{ cursor: 'default' }}>
                        {`${codes.slice(0, 1).join(', ')} +${codes.length - 1}`}
                    </Typography>
                </Tooltip>
            );
        },
    },
    {
        id: 'payer',
        header: 'Payer',
        accessorKey: 'payer',
        width: 220,
        minWidth: 110,
        cell: (value) => (
            <Typography>
                {value}
            </Typography>
        ),
    },
    {
        id: 'billedAmount',
        header: 'Billed',
        accessorKey: 'billedAmount',
        width: 120,
        minWidth: 120,
        cell: (value) => (
            <Typography
                sx={{
                    fontWeight: 500,
                    textAlign: 'right',
                }}
            >
                {formatCurrency(value)}
            </Typography>
        ),
    },
    {
        id: 'paidAmount',
        header: 'Paid',
        accessorKey: 'paidAmount',
        width: 120,
        minWidth: 120,
        cell: (value) => (
            <Typography
                sx={{
                    fontWeight: 500,
                    textAlign: 'right',
                    color: value > 0 ? 'success.main' : 'text.secondary',
                }}
            >
                {formatCurrency(value)}
            </Typography>
        ),
    },
    {
        id: 'patientBalance',
        header: 'Patient Balance',
        accessorKey: 'patientBalance',
        width: 130,
        minWidth: 130,
        cell: (value) => (
            <Typography
                sx={{
                    fontWeight: 500,
                    textAlign: 'right',
                    color: value > 0 ? '#D97706' : 'text.secondary',
                }}
            >
                {formatCurrency(value)}
            </Typography>
        ),
    },
    {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        width: 140,
        minWidth: 140,
        cell: (value) => <StatusChip status={value} fitContent />,
    },
    {
        id: 'denialReason',
        header: 'Denial Codes',
        accessorKey: 'denialReason',
        width: 290,
        minWidth: 290,
        cell: (value) => {
            const codes = value ? value.split(/[,;]\s*/).filter(Boolean) : [];
            
            return (
                <Typography
                    title={value || ''}
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.4,
                    }}
                >
                    {codes.map((code: string, idx: number) => (
                        <Typography
                            key={idx}
                            component="span"
                            sx={{
                                fontSize: 'inherit',
                                display: 'inline-block',
                            }}
                        >
                            {code.trim()}
                            {idx < codes.length - 1 && ', '}
                        </Typography>
                    ))}
                </Typography>
            );
        },
    },
    {
        id: 'assignedUser',
        header: 'Assignee',
        accessorKey: 'assignedUser',
        width: 200,
        minWidth: 200,
        cell: (value, row) => (
            <AssignedUserSelect
                claimId={row.id}
                currentUser={value}
            />
        ),
    },
    {
        // A plain date off the 837, not an instant -- formatDisplayDate like
        // DOS/DOB, so it is not shifted into the viewer's timezone (REV-2507).
        id: 'dateSubmitted',
        header: 'Submitted',
        accessorKey: 'dateSubmitted',
        width: 120,
        minWidth: 120,
        cell: (value) => (
            <Typography>
                {formatDisplayDate(value)}
            </Typography>
        ),
    },
    {
        id: 'appealLetter',
        header: 'Appeal letter',
        accessorKey: 'appealLetter',
        width: 130,
        minWidth: 130,
        cell: (value) => (
            <Typography>
                {value ? 'Yes' : 'No'}
            </Typography>
        ),
    },
    {
        id: 'appealed',
        header: 'Appealed',
        accessorKey: 'appealed',
        width: 110,
        minWidth: 110,
        cell: (value) => (
            <Typography>
                {value ? 'Yes' : 'No'}
            </Typography>
        ),
    },
    {
        // The recorded actions' types (REV-2628)
        id: 'actionType',
        header: 'Action type',
        accessorKey: 'actionTypes',
        width: 170,
        minWidth: 170,
        cell: (value) => (
            <Typography>
                {formatActionTypes(value)}
            </Typography>
        ),
    },
    {
        // Linked to its correct-and-resubmit counterpart
        id: 'linked',
        header: 'Linked',
        accessorKey: 'linked',
        width: 110,
        minWidth: 110,
        cell: (value) => (
            <Typography>
                {value ? 'Yes' : 'No'}
            </Typography>
        ),
    },
    {
        // The original's paid plus its linked corrected claim's (REV-2634);
        // null when the claim isn't the original of a live link
        id: 'paidInclResubmission',
        header: 'Paid incl. resubmission',
        accessorKey: 'paidInclResubmission',
        width: 190,
        minWidth: 190,
        cell: (value) => (
            <Typography
                sx={{
                    fontWeight: 500,
                    textAlign: 'right',
                    color: Number(value) > 0 ? 'success.main' : 'text.secondary',
                }}
            >
                {value != null ? formatCurrency(Number(value)) : '-'}
            </Typography>
        ),
    },
    {
        id: 'disposition',
        header: 'Disposition',
        accessorKey: 'disposition',
        width: 150,
        minWidth: 150,
        cell: (value) => (
            <Typography>
                {value || '-'}
            </Typography>
        ),
    },
    {
        id: 'createdAt',
        header: 'Imported',
        accessorKey: 'createdAt',
        width: 160,
        minWidth: 160,
        cell: (value) => (
            <Typography>
                {value ? formatInstant(value, { dateOnly: true }) : '-'}
            </Typography>
        ),
    },
    {
        id: 'updatedAt',
        header: 'Last updated',
        accessorKey: 'updatedAt',
        width: 190,
        minWidth: 190,
        cell: (value) => (
            <Typography>
                {value ? formatInstant(value, { dateOnly: true }) : '-'}
            </Typography>
        ),
    },
];
